/**
 * Select-your-car "fitment finder" presentation layer (sections/car-selector.liquid).
 * car-selector.js owns the cascading dropdowns and the submit/redirect; this file only reacts
 * to them: the tachometer progress gauge, the popular-brand quick picks, the live button label
 * and hint, and the reset link. It must load after car-selector.js so its change listeners run
 * after the dropdowns have been refilled.
 */
(function () {
  var MAX_RPM = 8;
  // Needle position (x1000 r/min) per number of fields chosen; 6.5+ is the redline.
  var STEP_RPM = [0.8, 3.2, 5.4, 7.3];
  var QUICK_PICK_LIMIT = 6;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function rpmToAngle(rpm) { return -120 + (rpm / MAX_RPM) * 240; }

  function initFinder(root) {
    var form = root.querySelector('[data-car-selector-form]');
    var brandSelect = root.querySelector('[data-car-brand]');
    var modelSelect = root.querySelector('[data-car-model]');
    var yearSelect = root.querySelector('[data-car-year]');
    if (!form || !brandSelect || !modelSelect || !yearSelect) return;

    var copy = form.dataset;
    var needle = root.querySelector('[data-gauge-needle]');
    var progress = root.querySelector('[data-gauge-progress]');
    var readoutTop = root.querySelector('[data-readout-top]');
    var readoutMain = root.querySelector('[data-readout-main]');
    var readoutStatus = root.querySelector('[data-readout-status]');
    var ctaLabel = root.querySelector('[data-cta-label]');
    var hint = root.querySelector('[data-finder-hint]');
    var resetBtn = root.querySelector('[data-finder-reset]');
    var chipsWrap = root.querySelector('[data-quick-chips]');
    var chips = [];

    function fill(template, value) { return (template || '').replace('__CAR__', value).replace('__N__', value); }

    function setGauge(rpm) {
      if (needle) needle.style.transform = 'rotate(' + rpmToAngle(rpm).toFixed(1) + 'deg)';
      if (progress) progress.style.strokeDashoffset = (100 - (rpm / MAX_RPM) * 100).toFixed(1);
    }

    function currentCar() {
      var brand = brandSelect.value;
      var model = brand ? modelSelect.value : '';
      var year = model ? yearSelect.value : '';
      return { brand: brand, model: model, year: year, step: year ? 3 : model ? 2 : brand ? 1 : 0 };
    }

    function render() {
      var car = currentCar();
      root.dataset.step = car.step;

      [[brandSelect, car.brand], [modelSelect, car.model], [yearSelect, car.year]].forEach(function (pair) {
        var seg = pair[0].closest('.car-finder__seg');
        if (seg) seg.classList.toggle('is-set', !!pair[1]);
      });

      if (ctaLabel) {
        if (car.step === 0) ctaLabel.textContent = copy.ctaAll;
        else if (car.step === 1) ctaLabel.textContent = fill(copy.ctaPartial, car.brand);
        else if (car.step === 2) ctaLabel.textContent = fill(copy.ctaPartial, car.model);
        else ctaLabel.textContent = fill(copy.ctaFull, car.year + ' ' + car.model);
      }
      if (hint) {
        if (car.step === 0) hint.textContent = copy.hintNone;
        else if (car.step === 1) hint.textContent = copy.hintBrand;
        else if (car.step === 2) hint.textContent = copy.hintModel;
        else hint.textContent = fill(copy.hintFull, car.year + ' ' + car.brand + ' ' + car.model);
      }
      if (resetBtn) resetBtn.hidden = car.step === 0;

      if (readoutTop) readoutTop.textContent = car.step >= 2 ? car.brand + (car.year ? ' · ' + car.year : '') : copy.readoutLabel;
      if (readoutMain) readoutMain.textContent = car.step === 0 ? copy.readoutEmpty : car.step === 1 ? car.brand : car.model;
      if (readoutStatus) readoutStatus.textContent = car.step === 3 ? copy.statusFull : fill(copy.statusProgress, String(car.step));

      chips.forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.dataset.brand === car.brand ? 'true' : 'false');
      });

      if (!root.classList.contains('is-sweeping')) setGauge(STEP_RPM[car.step]);
    }

    // Popular brands: the ones with the most models in the vehicle catalogue, shown as one-tap picks.
    if (chipsWrap) {
      var vehicles = [];
      try { vehicles = JSON.parse(root.querySelector('[data-vehicle-data]').textContent); } catch (e) {}
      var counts = {};
      vehicles.forEach(function (v) { if (v.brand) counts[v.brand] = (counts[v.brand] || 0) + 1; });
      Object.keys(counts)
        .sort(function (a, b) { return counts[b] - counts[a] || a.localeCompare(b); })
        .slice(0, QUICK_PICK_LIMIT)
        .forEach(function (brand) {
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'car-finder__chip';
          chip.dataset.brand = brand;
          chip.textContent = brand;
          chip.setAttribute('aria-pressed', 'false');
          chipsWrap.appendChild(chip);
          chips.push(chip);
        });
      var quickPick = chipsWrap.closest('[data-quick-pick]');
      if (quickPick) quickPick.hidden = chips.length === 0;

      chipsWrap.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-brand]');
        if (!chip) return;
        brandSelect.value = chip.dataset.brand === brandSelect.value ? '' : chip.dataset.brand;
        brandSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        brandSelect.value = '';
        brandSelect.dispatchEvent(new Event('change', { bubbles: true }));
        brandSelect.focus();
      });
    }

    [brandSelect, modelSelect, yearSelect].forEach(function (select) {
      select.addEventListener('change', render);
    });
    render();

    // Ignition sweep: the first time the panel scrolls into view the needle revs to the
    // redline and settles back, like an instrument cluster self-test.
    if (needle && !reduceMotion && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        root.classList.add('is-sweeping');
        setTimeout(function () { setGauge(MAX_RPM); }, 250);
        setTimeout(function () {
          root.classList.remove('is-sweeping');
          render();
        }, 1150);
      }, { threshold: 0.4 });
      observer.observe(root);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-car-finder]').forEach(initFinder);
  });
})();
